/**
 * Sidebar Tree Commands - Bulk expand/collapse folder operations
 *
 * These commands operate on the dataspaceTreeStore, which persists
 * folder expansion state to the dataspace_edges backend collection.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useDataspaceTreeStore from '../../stores/dataspaceTreeStore';

const log = createFileLogger('SIDEBAR_TREE_COMMANDS', { enabled: false });

export const sidebarTreeCommands: SelfContainedCommand[] = [
  {
    id: 'sidebar_expandAllFolders',
    description: 'Expand all folders in the sidebar file tree to show their contents',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('All folders expanded', { expandedCount: 5 }),
          description: 'Expand all folders',
          context: 'When user wants to see all files in the sidebar'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 200, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { expandAllNodes, initialized } = useDataspaceTreeStore.getState();

      if (!initialized) {
        return createErrorResult(
          'Sidebar tree not yet initialized',
          'The file tree has not loaded yet. Please wait a moment and try again.',
          'precondition'
        );
      }

      log.log('[sidebar_expandAllFolders] Starting bulk expand');
      const { count } = expandAllNodes();
      log.log('[sidebar_expandAllFolders] Done, expanded:', count, 'nodes, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult(
        count > 0
          ? `✅ Expanded ${count} folder${count !== 1 ? 's' : ''}`
          : 'All folders are already expanded',
        { expandedCount: count },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  } as SelfContainedCommand,

  {
    id: 'sidebar_collapseAllFolders',
    description: 'Collapse all folders in the sidebar file tree',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: { parameters: [] },

    examples: {
      good: [
        {
          input: {},
          expectedOutput: createSuccessResult('All folders collapsed', { collapsedCount: 5 }),
          description: 'Collapse all folders',
          context: 'When user wants a compact view of the sidebar'
        }
      ],
      bad: []
    },

    performance: { expectedDuration: 200, complexity: 'low' },

    handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { collapseAllNodes, initialized } = useDataspaceTreeStore.getState();

      if (!initialized) {
        return createErrorResult(
          'Sidebar tree not yet initialized',
          'The file tree has not loaded yet. Please wait a moment and try again.',
          'precondition'
        );
      }

      log.log('[sidebar_collapseAllFolders] Starting bulk collapse');
      const { count } = collapseAllNodes();
      log.log('[sidebar_collapseAllFolders] Done, collapsed:', count, 'nodes, elapsed:', Date.now() - startTime, 'ms');

      return createSuccessResult(
        count > 0
          ? `✅ Collapsed ${count} folder${count !== 1 ? 's' : ''}`
          : 'All folders are already collapsed',
        { collapsedCount: count },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  } as SelfContainedCommand,
];
